import React, { useRef } from "react";
import "./RealEngineering.css";
import { Helmet } from "react-helmet-async";

function RealEngineering() {
    const currentUrl = window.location.href;
  const experiences = [
    {
      icon: "🏭",
      title: "Live Engineering Projects",
      text: "Observe live engineering projects and understand how professional teams execute real-world work.",
    },
    {
      icon: "🤝",
      title: "Multidisciplinary Teams",
      text: "Understand how Mechanical, Electrical, and IT teams work together on complex engineering projects.",
    },
    {
      icon: "⚙️",
      title: "Industry Workflows",
      text: "Learn the workflows, processes, and practices followed by leading engineering companies.",
    },
    {
      icon: "💻",
      title: "Advanced Engineering Tools",
      text: "See CAD, CAE, PLM, automation, and other advanced engineering technologies used in real projects.",
    },
    {
      icon: "👨‍💼",
      title: "Meet Industry Experts",
      text: "Interact with experienced engineers and project leaders and learn from their industry experience.",
    },
    {
      icon: "🎯",
      title: "Career Insights",
      text: "Understand industry expectations and the skills companies look for in fresh engineering graduates.",
    },
  ];

  const benefits = [
    "Bridge the gap between college and industry",
    "Understand real project execution",
    "Build confidence before entering the job market",
    "Make better career decisions",
    "Enhance your resume with industry exposure",
  ];

  const attendees = [
    "Mechanical Engineering Students",
    "Electrical Engineering Students",
    "Electronics & Communication Students",
    "Computer Science & IT Students",
    "Fresh Graduates",
    "Young Engineers looking for industry exposure",
  ];

  const highlights = [
    {
      icon: "📍",
      title: "Location",
      value: "Bangalore",
    },
    {
      icon: "🕘",
      title: "Duration",
      value: "1 Full Day",
    },
    {
      icon: "👨‍💼",
      title: "Experience",
      value: "Live Industry Exposure",
    },
    {
      icon: "🖥️",
      title: "Technology",
      value: "Advanced Engineering Software",
    },
    {
      icon: "🤝",
      title: "Interaction",
      value: "Industry Experts",
    },
    {
      icon: "📜",
      title: "Certificate",
      value: "Participation Certificate",
    },
    {
      icon: "💰",
      title: "Registration",
      value: "Affordable Registration Fee",
    },
     {
      icon: "📸",
      title: "Takeaway",
      value: "Real-World Project & Learning",
    },
  ];


  const nextSectionRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = (): void => {
    nextSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };


  return (

    <>
    <Helmet>

  {/* =========================================
      BASIC SEO
  ========================================= */}

  <title>
    Experience Real Engineering for a Day | PriceWorth
  </title>

  <meta
    name="description"
    content="Experience real engineering for a day in Bangalore. Get live industry exposure, observe real engineering projects, interact with industry experts, and learn how Mechanical, Electrical, Electronics, and IT teams work together."
  />

  <meta
    name="keywords"
    content="real engineering experience Bangalore, engineering industry exposure, engineering students Bangalore, mechanical engineering industry visit, electrical engineering industry exposure, engineering company visit, engineering internship exposure, CAD CAE PLM training, engineering students industry visit, engineering career exposure, industry visit Bangalore, PriceWorth engineering"
  />

  <meta
    name="robots"
    content="index, follow"
  />

  <link
    rel="canonical"
    href={currentUrl}
  />


  {/* =========================================
      OPEN GRAPH / FACEBOOK / WHATSAPP
  ========================================= */}

  <meta
    property="og:title"
    content="Experience Real Engineering for a Day | PriceWorth"
  />

  <meta
    property="og:description"
    content="Step inside a real engineering company in Bangalore. Experience live engineering projects, industry workflows, advanced engineering software, and interaction with experienced engineers."
  />

  <meta
    property="og:type"
    content="website"
  />

  <meta
    property="og:url"
    content={currentUrl}
  />

  <meta
    property="og:site_name"
    content="PriceWorth"
  />

  <meta
    property="og:locale"
    content="en_IN"
  />

  <meta
    property="og:image"
    content="https://priceworth.com/assets/engineering/real-engineering.webp"
  />

  <meta
    property="og:image:type"
    content="image/webp"
  />

  <meta
    property="og:image:width"
    content="1200"
  />

  <meta
    property="og:image:height"
    content="630"
  />

  <meta
    property="og:image:alt"
    content="Experience Real Engineering for a Day - PriceWorth"
  />


  {/* =========================================
      TWITTER / X
  ========================================= */}

  <meta
    name="twitter:card"
    content="summary_large_image"
  />

  <meta
    name="twitter:title"
    content="Experience Real Engineering for a Day | PriceWorth"
  />

  <meta
    name="twitter:description"
    content="Spend one day inside a professional engineering company in Bangalore and experience real projects, engineering workflows, advanced software, and industry interaction."
  />

  <meta
    name="twitter:image"
    content="https://priceworth.com/assets/engineering/real-engineering.webp"
  />

  <meta
    name="twitter:image:alt"
    content="Experience Real Engineering for a Day"
  />


  {/* =========================================
      STRUCTURED DATA
  ========================================= */}

  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Event",

      name: "Experience Real Engineering for a Day",

      description:
        "Spend one full day inside a professional engineering company in Bangalore and experience real-world engineering projects, industry workflows, advanced engineering software, and interaction with industry experts.",

      url: currentUrl,

      eventAttendanceMode:
        "https://schema.org/OfflineEventAttendanceMode",

      eventStatus:
        "https://schema.org/EventScheduled",

      location: {
        "@type": "Place",
        name: "PriceWorth",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Bangalore",
          addressCountry: "IN"
        }
      },

      organizer: {
        "@type": "Organization",
        name: "PriceWorth",
        url: "https://priceworth.com"
      },

      image: [
        "https://priceworth.com/assets/engineering/real-engineering.webp"
      ],

      inLanguage: "en-IN"
    })}
  </script>

</Helmet>
    
    <div className="real-engineering-page">

      {/* =========================================
          HERO SECTION
      ========================================= */}

      <section className="re-hero">

        <div className="re-hero-overlay"></div>

        <div className="re-container re-hero-content">

          <div className="re-badge">
            🏭 INDUSTRY EXPOSURE PROGRAM
          </div>

          <h1>
            Experience <span>Real Engineering</span> for a Day!
          </h1>

          <p className="re-hero-subtitle">
            Step Inside a Real Engineering Company.
            <br />
            Learn What Colleges Don't Teach.
          </p>

          <p className="re-hero-description">
            Spend one full day inside a professional engineering company
            in Bangalore and discover how engineers collaborate to deliver
            real-world projects for global clients.
          </p>

          <div className="re-hero-buttons">
            <button className="re-primary-btn">
              🚀 Register Now
            </button>

            <button className="re-secondary-btn" onClick={handleScroll}>
              Learn More ↓
            </button>
          </div>

          <div className="re-hero-stats">

            <div>
              <strong>1</strong>
              <span>Full Day</span>
            </div>

            <div>
              <strong>100%</strong>
              <span>Industry Exposure</span>
            </div>

            <div>
              <strong>🏭</strong>
              <span>Real Projects</span>
            </div>

          </div>

        </div>

      </section>


      {/* =========================================
          INTRO
      ========================================= */}

      <section className="re-section">

        <div className="re-container">

          <div className="re-section-heading" ref={nextSectionRef}>

            <span className="re-section-label">
              REAL INDUSTRY EXPERIENCE
            </span>

            <h2 >
              See How Engineering Works <span>Beyond the Classroom</span>
            </h2>

            <p>
              Are you a Mechanical, Electrical, CSE, or Engineering student
              wondering how real engineering projects are executed in industry?
            </p>

          </div>


          {/* Experience Cards */}

          <div className="re-experience-grid">

            {experiences.map((item, index) => (

              <div
                className="re-experience-card"
                key={index}
              >

                <div className="re-card-icon">
                  {item.icon}
                </div>

                <h3>
                  {item.title}
                </h3>

                <p>
                  {item.text}
                </p>

              </div>

            ))}

          </div>

        </div>

      </section>


      {/* =========================================
          WHY JOIN
      ========================================= */}

      <section className="re-why-section">

        <div className="re-container re-two-column">

          <div className="re-why-content">

            <span className="re-section-label">
              WHY JOIN?
            </span>

            <h2>
              Turn Engineering <span>Theory Into Reality</span>
            </h2>

            <p>
              Most engineering graduates know theory but have never seen
              how an actual engineering company functions.
            </p>

            <p>
              This unique industry exposure program helps you understand
              how engineering teams work together and how real projects
              move from concept to successful delivery.
            </p>

          </div>


          <div className="re-benefits-card">

            {benefits.map((benefit, index) => (

              <div
                className="re-benefit"
                key={index}
              >

                <div className="re-check">
                  ✓
                </div>

                <span>
                  {benefit}
                </span>

              </div>

            ))}

          </div>

        </div>

      </section>


      {/* =========================================
          WHO CAN ATTEND
      ========================================= */}

      <section className="re-section">

        <div className="re-container">

          <div className="re-section-heading">

            <span className="re-section-label">
              WHO CAN ATTEND?
            </span>

            <h2>
              Built for <span>Future Engineers</span>
            </h2>

          </div>


          <div className="re-attendees-grid">

            {attendees.map((person, index) => (

              <div
                className="re-attendee-card"
                key={index}
              >

                <div className="re-attendee-number">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <span>
                  {person}
                </span>

             

              </div>

            ))}

          </div>

        </div>

      </section>


      {/* =========================================
          PROGRAM HIGHLIGHTS
      ========================================= */}

      <section className="re-highlight-section">

        <div className="re-container">

          <div className="re-section-heading light">

            <span className="re-section-label">
              PROGRAM HIGHLIGHTS
            </span>

            <h2>
              One Day. <span>Real Industry Exposure.</span>
            </h2>

          </div>


          <div className="re-highlight-grid">

            {highlights.map((item, index) => (

              <div
                className="re-highlight-card"
                key={index}
              >

                <div className="re-highlight-icon">
                  {item.icon}
                </div>

                <div>

                  <small>
                    {item.title}
                  </small>

                  <strong>
                    {item.value}
                  </strong>

                </div>

              </div>

            ))}

          </div>

        </div>

      </section>


      {/* =========================================
          EXPERIENCE SECTION
      ========================================= */}

      <section className="re-experience-banner">

        <div className="re-container">

          <div className="re-banner-content">

            <span className="re-section-label">
              DON'T JUST LEARN ENGINEERING
            </span>

            <h2>
              Experience <span>Engineering.</span>
            </h2>

            <p>
              See how products are designed, drawings are created,
              engineering data is managed, and multidisciplinary teams
              collaborate to deliver successful projects.
            </p>

            <div className="re-banner-points">

              <span>✓ Real Projects</span>
              <span>✓ Real Engineers</span>
              <span>✓ Real Learning</span>

            </div>

          </div>

        </div>

      </section>


      {/* =========================================
          FINAL CTA
      ========================================= */}

      <section className="re-cta-section">

        <div className="re-container">

          <div className="re-cta-card">

            <div className="re-cta-icon">
              🚀
            </div>

            <h2>
              Ready to Experience Real Engineering?
            </h2>

            <p>
              Spend One Day. Gain Industry Insights That Can
              Shape Your Career.
            </p>

            <div className="re-limited">
              🔥 Limited Seats Available. Register Early!
            </div>

            <button className="re-cta-button">
              Register Now →
            </button>

          </div>

        </div>

      </section>

    </div>
    </>
  );
}

export default RealEngineering;